import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";

interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
const InstallContext = createContext<{ standalone: boolean; available: boolean; install: () => Promise<void>; error: string | null }>({
  standalone: false, available: false, install: async () => {}, error: null,
});
function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function InstallationProvider({ children }: { children: ReactNode }) {
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [standalone, setStandalone] = useState(isStandalone);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const media = window.matchMedia("(display-mode: standalone)");
    const update = () => setStandalone(isStandalone());
    const offer = (event: Event) => { event.preventDefault(); setPrompt(event as InstallPrompt); };
    const installed = () => { setPrompt(null); setStandalone(true); };
    media.addEventListener("change", update);
    window.addEventListener("beforeinstallprompt", offer);
    window.addEventListener("appinstalled", installed);
    return () => {
      media.removeEventListener("change", update);
      window.removeEventListener("beforeinstallprompt", offer);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);
  async function install() {
    if (!prompt) return;
    setError(null);
    setPrompt(null);
    try { await prompt.prompt(); await prompt.userChoice; }
    catch { setError("Use your browser menu to install CheesyApps instead."); }
  }
  return <InstallContext.Provider value={{ standalone, available: Boolean(prompt), install, error }}>{children}</InstallContext.Provider>;
}

export function InstallationHelp() {
  const { standalone, available, install, error } = useContext(InstallContext);
  const apple = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (standalone) return null;
  return <details className="rounded-xl border border-border bg-card px-4 text-left">
    <summary className="flex min-h-11 cursor-pointer items-center gap-2 py-3 text-sm font-bold"><Download className="size-4" />Install CheesyApps</summary>
    <div className="space-y-3 pb-4 text-sm">
      <p>Add CheesyApps to your home screen for quick access. An internet connection is required.</p>
      {available && <Button onClick={() => void install()}>Install app</Button>}
      {apple ? <p>In Safari, open the Share menu, choose <strong>Add to Home Screen</strong>, leave <strong>Open as Web App</strong> enabled if shown, then tap <strong>Add</strong>.</p>
        : <><p>On Android, open CheesyApps in Chrome, open its menu, then choose <strong>Add to home screen</strong> or <strong>Install app</strong>.</p><p>On iPhone or iPad, use Safari’s Share menu → <strong>Add to Home Screen</strong>.</p></>}
      {error && <p role="alert">{error}</p>}
      <p className="text-xs text-muted-foreground">Installation is optional. You can keep using CheesyApps in your browser.</p>
    </div>
  </details>;
}
